import React from 'react';
import posthog from 'posthog-js';
import App from './src/containers/App';
import { onWindowLoadEvent, performDeviceIdentification, setPostHogInitialized } from './src/components/Analytics';
import { initGlobalBehaviorSignal } from './src/components/BehaviorSignalProvider';
import { installClarityProxy, initClarity } from './src/components/clarityProxy';
import { SITE_ROOT, IS_DEVELOPMENT, IS_ALPHA_STAGE, CLARITY_PROJECT_ID } from './src/api-config.mjs';
import log from 'loglevel';
import "@fontsource/sarabun/latin-400.css";


export const wrapRootElement = (props) => (
  <App {...props} browser>{props.element}</App>
);

export const onClientEntry = () => {
  log.debug("onClientEntry");

  // PostHog product analytics — proxied through first-party domain to bypass ad blockers.
  // Docs: https://posthog.com/docs/libraries/js
  // Proxy docs: https://posthog.com/docs/advanced/proxy
  if (!IS_DEVELOPMENT) {
    const posthogApiKey = IS_ALPHA_STAGE
      ? 'phc_94at20iKfqs2wcx3x9VDyqtmHADD1YvzxGnIxAE8qND'  // Alpha project
      : 'phc_WgVSA3kzH5x5U9U22VT4OhpYZgv0LjHMDtm2M95G6FD'; // Prod project
    posthog.init(posthogApiKey, {
      api_host: `${SITE_ROOT}/pd`,       // Reverse proxy via CloudFront (see cca repo backend.ts)
      ui_host: 'https://us.posthog.com',  // Required for toolbar/surveys when using a proxy
      person_profiles: 'identified_only', // Only create person profiles for identified users
    });
    setPostHogInitialized();

    // Microsoft Clarity — proxied through first-party domain to bypass ad
    // blockers. installClarityProxy() must run before initClarity() so the
    // runtime's telemetry is rewritten. Skipped entirely until a per-stage
    // CLARITY_PROJECT_ID is configured (so we don't patch globals for nothing).
    if (CLARITY_PROJECT_ID) {
      installClarityProxy();
      initClarity(CLARITY_PROJECT_ID);
    }
  }

  performDeviceIdentification();
  window.addEventListener("load", onWindowLoadEvent);

  // Initialize global behavior signal collector
  // All API requests will automatically include the behavior signal header
  initGlobalBehaviorSignal({
    enabled: true,  // Set to false to disable
    debug: false,   // Set to true for development debugging
  });
};

export const onInitialClientRender = () => {
  log.debug("ReactDOM.render has executed");
};

// GA4 loses UTM attribution across a login redirect because it starts a new
// session with no UTM params in the URL. Stash the first-landing UTM params in
// localStorage (sessionStorage doesn't survive a closed tab, which loses the
// UTM if the user closes the tab between clicking the ad and logging in) and
// re-push them to the dataLayer on every subsequent route change that lacks
// its own UTM params, so a GTM-side GA4 event tag can re-attribute the
// restored session instead of falling back to Direct.
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const UTM_STORAGE_KEY = 'ec_utm_params';

export const onRouteUpdate = ({ location }) => {
  const params = new URLSearchParams(location.search);
  const currentUTM = {};
  let hasUTM = false;
  UTM_PARAMS.forEach(param => {
    const value = params.get(param);
    if (value) {
      currentUTM[param] = value;
      hasUTM = true;
    }
  });

  if (hasUTM) {
    try {
      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(currentUTM));
    } catch (e) {}
  } else {
    try {
      const stored = localStorage.getItem(UTM_STORAGE_KEY);
      if (stored) {
        const utm = JSON.parse(stored);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'utm_restored',
          utm_source: utm.utm_source || '',
          utm_medium: utm.utm_medium || '',
          utm_campaign: utm.utm_campaign || '',
          utm_content: utm.utm_content || '',
          utm_term: utm.utm_term || ''
        });
      }
    } catch (e) {}
  }
};

export const disableCorePrefetching = () => {
  log.debug("disableCorePrefetching() -> return true");
  return true;
};
